import { DateRange } from '@mui/x-date-pickers-pro';
import { TZDate } from '@repo/tzdate';
import { useEffect, useMemo, useState } from 'react';
import { localeAtom } from '@repo/i18n';
import {
    BilberrySettings,
    BilberryPromoCodeStatus,
    GuestInfo,
    Product,
    Package,
    Timeslot,
    ProductInstance,
    ProductSearch,
    BilberryNextProductAvailability,
    BilberryNextPackageAvailability,
    TicketOptionWithQuantity,
} from '@repo/types';
import useSWR, { SWRConfiguration } from 'swr';
import { useAtom } from 'ximple';
import { groupProductInstancesByResourceCollectionId } from '../../../ProductMapper';
import { configurationAtom } from '../../../widgetsConfiguration';
import { get, getRequest } from '../../utils/api-client-common';
import { getDefaultHotelRequest, hotelDateParams } from '../../utils/bilberry-hotels-api-helpers';
import {
    getProduct,
    getProducts,
    getAvailableProducts,
    getUpcomingTours,
    getUpcomingToursRequest,
    getProductInstanceByIds,
    getSmartEvent,
    getSmartEvents,
    getProductSearch,
    getProductSearchRequest,
    getAvailablePackageProducts,
    getPackageAvailability,
    getAvailableTimeslots,
    getPackageProduct,
    getPackageProducts,
    getBilberrySettings,
    getPromoCode,
    getAccommodationProducts,
    getNextProductAvailabilityByProductIds,
    getNextProductAvailability,
    getNextPackageAvailability,
    getNextPackageAvailabilityByIds,
    getExtraProductInstancesByIds,
} from '../../fetchers/product';
import { sortBy } from 'lodash-es';
import { useOctoAvailabilities, useOctoNextAvailability, useOctoProduct } from '../octo';

export function useLocaleAndConfigCacheBuster() {
    const [{ locale }] = useAtom(localeAtom);
    const [config] = useAtom(configurationAtom);
    return (
        locale +
        (config.siteKey ?? '') +
        config.timezone +
        config.companyKey +
        config.bilberryBaseApiUrl +
        config.bilberryAccessToken
    );
}

export function useProduct(
    id: string | undefined,
    type?: 'accommodation' | 'timeslot',
    dateRange?: DateRange<TZDate> | null,
    guests?: number,
) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const url =
        '/api/products/' +
        id +
        type +
        dateRange?.[0]?.toISOString() +
        dateRange?.[1]?.toISOString() +
        guests +
        localeAndConfigCacheBuster;

    const { data, isLoading, error } = useSWR<Product | null>(
        id ? url : null,
        () => getProduct(id, type, dateRange, guests),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            keepPreviousData: true,
            fetcher: get,
        },
    );

    return {
        data: data ?? null,
        isLoading,
        error,
    };
}

export function useProducts({
    ids,
    collectionId,
    isTimeslots,
}: {
    ids: string[];
    collectionId?: string;
    isTimeslots?: boolean;
}) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey =
        '/api/products/' + ids?.join('') + collectionId + isTimeslots + localeAndConfigCacheBuster;
    const { data, isLoading, error } = useSWR<Product[]>(
        ids.length > 0 || collectionId ? cacheKey : null,
        async () => getProducts(ids, collectionId, isTimeslots),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data: data ?? [],
        isLoading,
        error,
    };
}

export function useAvailabilities(
    product: Product | null,
    from: TZDate | null,
    to: TZDate | null,
    allowClosed?: boolean,
    quantities?: TicketOptionWithQuantity[],
    selectedOptionId?: string,
) {
    const config = configurationAtom.subject.value;

    const fetchFromOcto = config.octo;
    const octoProductId = fetchFromOcto && product && from && to ? product.id : undefined;

    const { data: octoProduct } = useOctoProduct(octoProductId);

    const octoAvailabilities = useOctoAvailabilities(
        from,
        to,
        octoProduct,
        quantities,
        selectedOptionId,
    );

    const [availabilities, setAvailabilities] = useState<ProductInstance[]>([]);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey =
        product && from && to && !fetchFromOcto
            ? `/api/products/${
                  product.id + product.type + product.title
              }/availability?${from.toISOString()}${to.toISOString()}${
                  allowClosed ? '&allowClosed=true' : ''
              }${localeAndConfigCacheBuster}`
            : null;
    const { data, isLoading, error } = useSWR<ProductInstance[]>(
        cacheKey,
        () => getAvailableProducts(product, from, to, 1, allowClosed),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            keepPreviousData: true,
            refreshInterval: 0,
            revalidateIfStale: false,
            // Only do a simple strict equal when comparing, since the mapped value is too big/recursive and
            // can't use the default deep-equal check done by SWR (throws errors because of size).
            compare: (a, b) => a === b,
        },
    );

    useEffect(() => {
        if (data) {
            setAvailabilities((prev) => {
                const newArr = [...prev];
                for (const product of data) {
                    const index = newArr.findIndex((x) => x.id === product.id);
                    if (index !== -1) {
                        newArr[index] = product;
                    } else {
                        newArr.push(product);
                    }
                }
                const grouped = groupProductInstancesByResourceCollectionId(newArr);
                return sortBy(grouped, (x) => x.start);
            });
        }
    }, [data]);

    return {
        data: octoAvailabilities ?? availabilities,
        error, // TODO: Octo error state?
        isLoading, // TODO: Octo loading state?
    };
}

export function useAvailableTimeslots(
    productInstance?: ProductInstance,
    config: SWRConfiguration = {},
) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = `/api/v2/membership/projects/${productInstance?.id}/timeslots${localeAndConfigCacheBuster}`;

    const { data, error, isLoading } = useSWR<Timeslot[] | undefined>(
        productInstance ? cacheKey : null,
        () => getAvailableTimeslots(productInstance),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            ...config,
        },
    );

    return {
        data,
        error,
        isLoading,
    };
}

export function usePackageAvailability(
    pkg: Package | null,
    from: TZDate | null,
    to: TZDate | null,
    onSuccess?: (data: ProductInstance[]) => void,
) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = pkg
        ? `/api/packages/${
              pkg.id
          }/availability-multi${localeAndConfigCacheBuster}${from?.toISOString()}${to?.toISOString()}`
        : '';
    const { data, error, isLoading } = useSWR<ProductInstance[]>(
        cacheKey,
        () => getPackageAvailability(pkg, from, to),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            keepPreviousData: true,
            onSuccess,
        },
    );

    return {
        data,
        error,
        isLoading,
    };
}

export function useAvailablePackageProducts(pkg: Package | null, date: TZDate | null) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = pkg
        ? 'available-package-products' + pkg.id + date?.toISOString() + localeAndConfigCacheBuster
        : null;
    const { data, error, isLoading } = useSWR<ProductInstance[]>(
        cacheKey,
        () => getAvailablePackageProducts(pkg, date),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data: data ?? [],
        error,
        isLoading,
    };
}

export function useProductSearch(filters: ProductSearch | null, doNotRun = false) {
    const { url } = getProductSearchRequest(filters);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();

    const { data, error, isLoading } = useSWR<Product[]>(
        doNotRun ? null : url + localeAndConfigCacheBuster,
        () => getProductSearch(filters),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
    };
}

export function useSmartEvent(eventId: number) {
    const [locale] = useAtom(localeAtom);
    const [config] = useAtom(configurationAtom);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const { url } = getRequest(
        `/api/webevents/${eventId}${localeAndConfigCacheBuster}`,
        locale.locale,
        config,
    );

    const { data, error, isLoading } = useSWR(
        eventId ? url : null,
        async () => getSmartEvent(eventId),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useSmartEvents(eventIds: string[]) {
    const [locale] = useAtom(localeAtom);
    const [config] = useAtom(configurationAtom);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const { url } = getRequest(
        `/api/webevents?ids=${eventIds.join(',')}${localeAndConfigCacheBuster}`,
        locale.locale,
        config,
    );

    const { data, error, isLoading } = useSWR(url, async () => getSmartEvents(eventIds), {
        shouldRetryOnError: false,
        revalidateOnFocus: false,
    });

    return {
        data: data?.data ?? [],
        isLoading,
        error,
    };
}

export function useProductInstancesByIds(ids: string[]) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey =
        ids.length > 0 ? `/api/products?ids=${ids.join(',')}${localeAndConfigCacheBuster}` : null;
    const { data, error, isLoading } = useSWR<ProductInstance[]>(
        cacheKey,
        () => getProductInstanceByIds(ids),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data: data ?? [],
        isLoading,
        error,
    };
}

export function useExtraProductInstancesByIds(ids: string[]) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey =
        ids.length > 0 ? `/api/products?ids=${ids.join(',')}${localeAndConfigCacheBuster}` : null;
    const { data, error, isLoading } = useSWR<ProductInstance[]>(
        cacheKey,
        () => getExtraProductInstancesByIds(ids),
        {
            shouldRetryOnError: false,
            // We prevent this hook from revalidating because the mapped object is too
            // big to be compared with the swr default-comparator and will cause an error.
            // This means that this hook will only fetch once for each cache-key.
            //
            // @todo: When we eventually refactor fetchers and move mappers to hooks instead,
            // we can allow revalidation again.
            revalidateOnFocus: false,
            revalidateIfStale: false,
        },
    );

    return {
        data: data ?? [],
        isLoading,
        error,
    };
}

export function useUpcomingTours(
    productCatalogIds: number[],
    startDate: TZDate,
    endDate: TZDate,
    productCatalogUrls?: string[],
    allowClosed?: boolean,
) {
    const { url } = getUpcomingToursRequest(productCatalogIds, startDate, endDate, allowClosed);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();

    const { data, error, isLoading } = useSWR<ProductInstance[]>(
        url + localeAndConfigCacheBuster,
        async () =>
            getUpcomingTours(
                productCatalogIds,
                startDate,
                endDate,
                productCatalogUrls,
                allowClosed,
            ),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data: data ?? [],
        error,
        isLoading,
    };
}

export function useBilberrySettings() {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const { data, error, isLoading } = useSWR<BilberrySettings>(
        '/api/settings' + localeAndConfigCacheBuster,
        () => getBilberrySettings(),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        error,
        isLoading,
    };
}

export function usePromocodeStatus(promoCode: string | undefined, refreshInterval?: number) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = `/api/v2/promo-code/${promoCode}${localeAndConfigCacheBuster}`;
    const { data, error, mutate, isLoading } = useSWR<BilberryPromoCodeStatus>(
        promoCode ? cacheKey : null,
        () => getPromoCode(promoCode ?? ''),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            refreshInterval,
        },
    );

    return {
        data,
        mutate,
        error,
        isLoading,
    };
}

export function usePackageProduct(id: string) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = `/api/packages/${id}${localeAndConfigCacheBuster}`;
    const { data, isLoading, error } = useSWR<Package>(cacheKey, () => getPackageProduct(id), {
        shouldRetryOnError: false,
        revalidateOnFocus: false,
    });
    return {
        data,
        isLoading,
        error,
    };
}

export function usePackageProducts(ids?: string[]) {
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();
    const cacheKey = '/api/packages?' + 'ids=' + ids?.join(',') + '&' + localeAndConfigCacheBuster;
    const { data, error, isLoading } = useSWR<Package[]>(cacheKey, () => getPackageProducts(ids));

    return {
        data: data ?? [],
        error,
        isLoading,
    };
}

export function useNextProductAvailabilityByProductIds(
    productIds: string[],
    includeWaitlist?: boolean,
) {
    const cacheKey =
        productIds.length > 0
            ? `useNextProductAvailabilityByProductIds-${productIds.join(',')}-${
                  includeWaitlist ? 'waitlist' : 'available'
              }`
            : null;
    const { data, isLoading, error } = useSWR<BilberryNextProductAvailability[] | null>(
        cacheKey,
        () => getNextProductAvailabilityByProductIds(productIds, includeWaitlist),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useNextProductAvailability(
    productId?: string,
    includeWaitlist?: boolean,
    selectedOptionId?: string,
) {
    const config = configurationAtom.subject.value;

    const fetchFromOcto = config.octo;
    const octoProductId = fetchFromOcto && productId ? productId : undefined;

    const { data: octoProduct } = useOctoProduct(octoProductId);

    const nextAvailabilityRequest = useMemo(() => {
        if (!octoProduct) return undefined;

        // For products with multiple options, wait for selectedOptionId before fetching
        if (octoProduct.options.length > 1 && !selectedOptionId) {
            return undefined;
        }

        // Use selected option or default to first option
        const optionId = selectedOptionId ?? octoProduct.options[0]?.id;
        if (!optionId) return undefined;

        return {
            productId: octoProduct.id,
            optionId,
        };
    }, [octoProduct, selectedOptionId]);

    const {
        data: octoNextAvailabilityResponse,
        isLoading: octoLoading,
        error: octoError,
    } = useOctoNextAvailability(nextAvailabilityRequest);

    const octoNextAvailability = useMemo(() => {
        if (!octoNextAvailabilityResponse?.nextAvailableDate) return undefined;

        return {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            product_catalog_id: 0, // TODO: this doesn't fit well with octo
            // eslint-disable-next-line @typescript-eslint/naming-convention
            tour_id: 0, // TODO: this doesn't fit well with octo
            next: octoNextAvailabilityResponse.nextAvailableDate,
        } as BilberryNextProductAvailability;
    }, [octoNextAvailabilityResponse]);

    const cacheKey =
        productId && !fetchFromOcto
            ? `useNextProductAvailability-${productId}-${includeWaitlist ? 'waitlist' : 'available'}`
            : null;
    const {
        data: baseNextAvailability,
        isLoading,
        error,
    } = useSWR<BilberryNextProductAvailability | null>(
        cacheKey,
        () => {
            return getNextProductAvailability(productId!, includeWaitlist);
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    const nextAvailability = octoNextAvailability ? octoNextAvailability : baseNextAvailability;

    return {
        data: nextAvailability,
        isLoading: fetchFromOcto ? octoLoading : isLoading,
        error: fetchFromOcto ? octoError : error,
    };
}

export function useNextPackageAvailability(packageId?: string) {
    const cacheKey = packageId ? `useNextPackageAvailability-${packageId}` : null;
    const { data, isLoading, error } = useSWR<BilberryNextPackageAvailability | null>(
        cacheKey,
        () => getNextPackageAvailability(packageId!),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

export function useNextPackageAvailabilityByIds(packageIds: string[]) {
    const cacheKey =
        packageIds.length > 0 ? `useNextPackageAvailabilityByIds-${packageIds.join(',')}` : null;
    const { data, isLoading, error } = useSWR<BilberryNextPackageAvailability[] | null>(
        cacheKey,
        () => getNextPackageAvailabilityByIds(packageIds),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
        },
    );

    return {
        data,
        isLoading,
        error,
    };
}

////////////////////////////
// Visbook accommodations //
////////////////////////////

export function useAccommodationProducts(
    ids: string[],
    dateRange: DateRange<TZDate>,
    accommodationsInfo: GuestInfo[],
    active = true,
    onlyAvailable = false,
) {
    const [locale] = useAtom(localeAtom);
    const [config] = useAtom(configurationAtom);
    const localeAndConfigCacheBuster = useLocaleAndConfigCacheBuster();

    const urls = useMemo(
        () =>
            accommodationsInfo.map((accommodation: GuestInfo) => {
                const guestCount = accommodation.adults + accommodation.children.length;

                const queryParams = {
                    ...hotelDateParams(dateRange),
                    guests: guestCount,
                    units: 1,
                    onlyAvailableAccommodations: onlyAvailable,
                };

                const { url } = getDefaultHotelRequest(
                    `/accommodations`,
                    locale.locale,
                    config,
                    queryParams,
                );
                return url;
            }),
        [dateRange, accommodationsInfo, locale, config, onlyAvailable],
    );

    const { data, error, isLoading, mutate } = useSWR<ProductInstance[][][]>(
        active ? urls + localeAndConfigCacheBuster : null,
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            fetcher: () =>
                getAccommodationProducts(ids, dateRange, accommodationsInfo, onlyAvailable),
        },
    );

    const dataOrDefault = useMemo(() => data ?? [], [data]);

    return {
        data: dataOrDefault,
        error,
        isLoading,
        mutate,
    };
}
